import React, { useCallback, useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { Loader } from "../Components/Loader";
import { Button } from "./shared/ui-react/Button";
import { Button as MuiButton, Tooltip } from "@mui/material";
import {
  createNewConnection,
  deleteConnectionByUuid,
  getConnections,
} from "../repositories/InfrakitRepository";
import Popup from "./Popup";
import TextField from "@mui/material/TextField";
import { logOut } from "../utils/AxiosHandlers";
import { formatDateAndTime } from "../utils/FileUtils";
import clsx from "clsx";

export default function InfrakitProjects() {
  const navigate = useNavigate();

  const [connections, setConnections] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createConnectionOpen, setCreateConnectionOpen] = useState(false);
  const [connectionName, setConnectionName] = useState("");
  const [deleteConnection, setDeleteConnection] = useState<{
    uuid: string;
    name: string;
  }>();

  useEffect(() => {
    const authToken = localStorage.getItem("infrakitToken");
    const token = JSON.parse(authToken || "{}").accessToken;
    !token && navigate("/");

    const connectionUuid = localStorage.getItem("connectionUuid");
    !!connectionUuid && navigate("/trimble/login");
  }, [navigate]);

  const fetchConnections = useCallback(() => {
    const projectUuid = localStorage.getItem("infrakitProjectUuid");
    !projectUuid && navigate("/");

    setIsLoading(true);
    getConnections(projectUuid!)
      .then((data) => {
        setConnections(data);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [navigate]);

  useEffect(fetchConnections, [fetchConnections]);

  const onSelectConnection = (connectionUuid: string) => {
    localStorage.setItem("connectionUuid", connectionUuid);
    navigate("/trimble/login");
  };

  const handleCreateNewConnection = () => {
    const projectUuid = localStorage.getItem("infrakitProjectUuid");

    setIsLoading(true);
    createNewConnection(projectUuid!, connectionName)
      .catch(() => {})
      .then(fetchConnections)
      .then(() => {
        setCreateConnectionOpen(false);
        setConnectionName("");
      });
  };

  return (
    <div>
      <div className="header-button-container">
        <MuiButton
          variant="contained"
          className="header-button"
          onClick={logOut}
        >
          Logout
        </MuiButton>
        <MuiButton
          variant="contained"
          className="header-button"
          onClick={() => {
            localStorage.removeItem("connectionUuid");
            localStorage.removeItem("infrakitProjectUuid");
            navigate("/infrakit/projects");
          }}
        >
          Change Infrakit Project
        </MuiButton>
      </div>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            marginBottom: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <img
            src="https://infrakit.atlassian.net/s/tod1zk/b/4/4dfcd128b56e7dd5dce7882155d48d6a/_/jira-logo-scaled.png"
            alt="logo"
          />

          <div className="header">
            <h3>
              Choose or{" "}
              <Button
                className="btn"
                disabled={isLoading}
                onClick={() => setCreateConnectionOpen(true)}
                style={{ padding: "5px" }}
              >
                Create New
              </Button>{" "}
              connection:
            </h3>
          </div>
          <Loader isLoading={isLoading} fullWidth>
            <div className="list">
              {connections.map((e) => (
                <div
                  key={e.uuid}
                  className="list-item"
                  onClick={() => onSelectConnection(e.uuid)}
                >
                  <div
                    style={{ alignItems: "center" }}
                    className="display-flex w-100 gap-2"
                  >
                    <span className="list-item-name">
                      <div
                        className="display-flex"
                        style={{ alignItems: "center" }}
                      >
                        <p className="title m-0">{e.name}</p>
                      </div>
                      {e.updated && (
                        <p className="date-value m-0">
                          {formatDateAndTime(e.updated)}
                        </p>
                      )}
                    </span>
                    {e.auto_sync && (
                      <Tooltip
                        title="Auto Syncing is enabled for this connection"
                        placement="top"
                      >
                        <div className="circle-icon auto-sync ml-1 mr-1" />
                      </Tooltip>
                    )}
                    {e.status === "FAILED" && (
                      <Tooltip
                        title={
                          e.error_message
                            ? `Error message: ${e.error_message}`
                            : "Connection failed"
                        }
                        placement="top"
                      >
                        <div
                          className={clsx("circle-icon failed mr-1", {
                            "ml-1": !e.auto_sync,
                          })}
                        />
                      </Tooltip>
                    )}
                    <Button
                      className="btn"
                      disabled={isLoading}
                      onClick={(event: any) => {
                        event.stopPropagation();
                        setDeleteConnection(e);
                      }}
                      style={{
                        padding: "5px",
                        marginLeft: "auto",
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Loader>
        </Box>
        <Popup
          show={createConnectionOpen}
          title={"Create new connection"}
          loading={isLoading}
          children={
            <TextField
              required
              fullWidth
              id="name"
              label="Connection name"
              value={connectionName || ""}
              onChange={(e) => setConnectionName(e.target.value)}
              autoFocus
            />
          }
          onClose={() => {
            setCreateConnectionOpen(false);
            setConnectionName("");
          }}
          onConfirm={handleCreateNewConnection}
          disableSubmit={!connectionName}
        />
        <Popup
          show={!!deleteConnection}
          title={"Delete connection"}
          loading={isLoading}
          children={
            <p>
              Are you sure you want to delete "{deleteConnection?.name}"
              connection?
            </p>
          }
          onClose={() => {
            setDeleteConnection(undefined);
          }}
          onConfirm={() =>
            !!deleteConnection &&
            deleteConnectionByUuid(deleteConnection.uuid!)
              .then(() => {
                setDeleteConnection(undefined);
                fetchConnections();
              })
              .catch(() => {})
          }
        />
      </Container>
    </div>
  );
}
